// ABOUTME: Utility functions for calculating and managing tradesperson badge tiers
// ABOUTME: Based on save counts and creation dates

export type BadgeTier = 'popular' | 'neighbor-count' | 'new' | null;

export interface BadgeInfo {
  tier: BadgeTier;
  label?: string;
  count?: number;
}

/**
 * Calculates the badge tier for a tradesperson based on save count and creation date
 * @param saveCount The number of users who have saved this tradesperson
 * @param createdAt The creation date of the tradesperson record
 * @param allSaveCounts Array of all save counts for calculating percentiles
 * @param excludeNew If true, won't show "new" badges (used for username-based results)
 * @returns BadgeInfo object with tier and display information
 */
export function calculateBadgeTier(
  saveCount: number,
  createdAt: string | Date,
  allSaveCounts: number[],
  excludeNew = false
): BadgeInfo {
  // First check for popularity status (takes precedence over new)
  if (allSaveCounts.length > 0 && saveCount > 0) {
    // Sort save counts in descending order
    const sortedCounts = [...allSaveCounts].sort((a, b) => b - a);

    // Calculate percentile thresholds
    const topTwentyPercentIndex = Math.floor(sortedCounts.length * 0.2);
    const topFiftyPercentIndex = Math.floor(sortedCounts.length * 0.5);

    // Determine tier based on percentile
    if (saveCount >= sortedCounts[topTwentyPercentIndex] && saveCount > 5) {
      // Top 20% and at least 5 saves - POPULAR overrides NEW
      return {
        tier: 'popular',
        label: 'Popular'
      };
    } else if (saveCount >= sortedCounts[topFiftyPercentIndex] && saveCount >= 3) {
      // Top 50% with at least 3 saves - NEIGHBOR COUNT overrides NEW
      return {
        tier: 'neighbor-count',
        count: saveCount,
        label: `${saveCount} lists`
      };
    }
  }

  // Check if tradesperson is new (only if not excluded and not popular or neighbor-count)
  if (!excludeNew) {
    const createdDate = new Date(createdAt);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    if (createdDate > thirtyDaysAgo) {
      return {
        tier: 'new',
        label: 'New'
      };
    }
  }

  // No badge
  return { tier: null };
}

/**
 * Gets all save counts from an array of tradespeople
 * @param tradespeople Array of tradespeople with save_count property
 * @returns Array of save counts
 */
export function extractSaveCounts(tradespeople: Array<{ save_count?: number }>): number[] {
  return tradespeople
    .map(t => t.save_count || 0)
    .filter(count => count > 0);
}

/**
 * Adds badge info to an array of tradespeople
 * @param tradespeople Array of tradespeople to process
 * @param excludeNew If true, won't show "new" badges (used for username-based results)
 * @returns Array of tradespeople with badge_info property added
 */
export function addBadgeInfo<T extends { save_count?: number; created_at: string }>(
  tradespeople: T[],
  excludeNew = false
): Array<T & { badge_info: BadgeInfo }> {
  const allSaveCounts = extractSaveCounts(tradespeople);

  return tradespeople.map(tradesperson => ({
    ...tradesperson,
    badge_info: calculateBadgeTier(
      tradesperson.save_count || 0,
      tradesperson.created_at,
      allSaveCounts,
      excludeNew
    )
  }));
}
